import {cn} from '@features/ui/utils';
import {useContext, useEffect, useRef, type CSSProperties} from 'react';
import {useSetAtom} from 'jotai';
import {isEditingAtom} from '@features/chat/chatSelector';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';

function selectAll(element: HTMLElement) {
  const range = document.createRange();
  const selection = window.getSelection();

  if (selection === null) return;

  range.selectNodeContents(element);

  selection.removeAllRanges();
  selection.addRange(range);
}

export const InlineEditableText = ({
  value,
  isEditable,
  setIsEditable,
  onSave,
  className,
  style,
}: {
  value: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  onSave: (newValue: string) => void;
  className?: string;
  style?: CSSProperties;
}) => {
  const ref = useRef<HTMLElement>(null);
  const setIsEditing = useSetAtom(isEditingAtom);
  const readonly = useContext(ReadonlyContext);

  useEffect(() => {
    if (!isEditable) return;
    setIsEditing(true);
    if (ref.current === null) return;
    ref.current.focus();
    selectAll(ref.current);
  }, [isEditable, setIsEditing]);

  if (readonly.readonly) {
    return (
      <span
        className={cn(
          className,
          'overflow-hidden text-ellipsis whitespace-nowrap',
        )}
        style={style}
      >
        {value}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      suppressContentEditableWarning={true}
      contentEditable={isEditable}
      onDoubleClick={() => {
        setIsEditable(true);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        ref.current?.blur();
      }}
      onBlur={(event) => {
        if (ref.current !== null) ref.current.scrollLeft = 0;
        setIsEditable(false);
        setIsEditing(false);
        const {textContent} = event.currentTarget;
        if (!textContent || textContent === value) return;
        onSave(textContent.trim());
      }}
      className={cn(
        className,
        'overflow-hidden text-ellipsis whitespace-nowrap focus:-mx-1 focus:overflow-visible focus:rounded-sm focus:bg-white focus:px-1 focus:text-black focus:outline-none focus:ring-2 focus:ring-[var(--primary-hex)]',
      )}
      style={style}
    >
      {value}
    </span>
  );
};
